import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import s from './ui.module.css';

export interface Datum {
  label: string;
  value: number;
  title?: string;
}

export interface SeriesDef {
  key: string;
  label: string;
  color: string;
}

export interface GroupDatum {
  label: string;
  title?: string;
  /** Value per series key; `null` = not measurable yet (drawn as a hollow stub). */
  values: Record<string, number | null>;
}

const LEFT = 48;
const RIGHT = 8;
const TOP = 14;
const BOTTOM = 28;

/** 0 and a few round steps above the largest value. */
function ticks(max: number, fixedMax?: number): number[] {
  const top = fixedMax ?? max;
  if (top <= 0) return [0, 1];
  const raw = top / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const m = raw / pow;
  const step = (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * pow;
  const out: number[] = [];
  for (let v = 0; v < top + step * 0.999; v += step) out.push(Number(v.toFixed(6)));
  return fixedMax !== undefined ? out.filter((v) => v <= fixedMax + 1e-9) : out;
}

/** The container's width in px, so the chart is drawn 1:1 and its text stays a true 12px. */
function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(600);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => e && setW(Math.max(240, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

/** The plot frame shared by both bar charts: grid, y ticks, x labels, an optional dashed line, a tooltip. */
function Frame({
  height,
  labels,
  yMax,
  fixedMax,
  format,
  ariaLabel,
  line,
  children,
  tip,
}: {
  height: number;
  labels: string[];
  yMax: number;
  fixedMax?: number | undefined;
  format: (n: number) => string;
  ariaLabel: string;
  line?: { value: number; label: string } | undefined;
  children: (geo: { slot: number; x0: (i: number) => number; y: (v: number) => number; plotH: number; base: number }) => ReactNode;
  tip: { i: number; text: ReactNode; v: number } | null;
}) {
  const [ref, W] = useWidth();
  const t = ticks(Math.max(yMax, line?.value ?? 0), fixedMax);
  const top = t[t.length - 1]!;
  const plotH = height - TOP - BOTTOM;
  const base = TOP + plotH;
  const y = (v: number) => base - (top > 0 ? (v / top) * plotH : 0);
  const slot = (W - LEFT - RIGHT) / Math.max(1, labels.length);
  const x0 = (i: number) => LEFT + i * slot;
  // Thin the x labels on long ranges so they never collide.
  const every = Math.ceil(labels.length / Math.max(2, Math.floor((W - LEFT) / 56)));
  return (
    <div className={s.chartBox} ref={ref}>
      <svg className={s.chart} viewBox={`0 0 ${W} ${height}`} role="img" aria-label={ariaLabel}>
        {t.map((v) => (
          <g key={v}>
            <line className={s.chartGrid} x1={LEFT} x2={W - RIGHT} y1={y(v)} y2={y(v)} />
            <text className={s.chartTick} x={LEFT - 8} y={y(v) + 4} textAnchor="end">
              {format(v)}
            </text>
          </g>
        ))}
        <line className={s.chartAxis} x1={LEFT} x2={W - RIGHT} y1={base + 0.5} y2={base + 0.5} />
        {children({ slot, x0, y, plotH, base })}
        {line && line.value <= top && (
          <g>
            <line className={s.chartBudget} x1={LEFT} x2={W - RIGHT} y1={y(line.value)} y2={y(line.value)} />
            <text className={s.chartTick} x={W - RIGHT} y={y(line.value) - 5} textAnchor="end">
              {line.label}
            </text>
          </g>
        )}
        {labels.map((l, i) =>
          i % every === 0 ? (
            <text key={i} className={s.chartTick} x={x0(i) + slot / 2} y={height - 8} textAnchor="middle">
              {l}
            </text>
          ) : null,
        )}
      </svg>
      {tip && (
        <div className={s.tooltip} style={{ left: `${((x0(tip.i) + slot / 2) / W) * 100}%`, top: `${(y(tip.v) / height) * 100}%` }}>
          {tip.text}
        </div>
      )}
    </div>
  );
}

/** Vertical bars with a value axis and a tooltip on hover; scales to its container width. */
export function BarChart({ data, format, axisFormat, height = 220, ariaLabel, color = 'var(--chart-1)' }: { data: readonly Datum[]; format: (n: number) => string; axisFormat?: (n: number) => string; height?: number; ariaLabel: string; color?: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(0, ...data.map((d) => d.value));
  const h = hover !== null ? data[hover] : null;
  return (
    <Frame
      height={height}
      labels={data.map((d) => d.label)}
      yMax={max}
      format={axisFormat ?? format}
      ariaLabel={ariaLabel}
      tip={h && hover !== null ? { i: hover, v: h.value, text: h.title ?? `${h.label}：${format(h.value)}` } : null}
    >
      {({ slot, x0, y, base }) =>
        data.map((d, i) => {
          const w = Math.min(44, slot * 0.62);
          const top = d.value > 0 ? Math.min(y(d.value), base - 2) : base;
          return (
            <g key={d.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={x0(i)} y={14} width={slot} height={base - 14} fill="transparent" />
              <rect className={s.chartBar} x={x0(i) + (slot - w) / 2} y={top} width={w} height={base - top} rx={2} fill={color} />
            </g>
          );
        })
      }
    </Frame>
  );
}

/** Bars per group, side by side or stacked, with a legend, a value axis and an optional dashed line (a budget). */
export function GroupedBarChart({
  data,
  series,
  format,
  axisFormat,
  max: fixedMax,
  height = 220,
  ariaLabel,
  stacked = false,
  line,
}: {
  data: readonly GroupDatum[];
  series: readonly SeriesDef[];
  format: (n: number) => string;
  axisFormat?: (n: number) => string;
  max?: number;
  height?: number;
  ariaLabel: string;
  stacked?: boolean;
  line?: { value: number; label: string };
}) {
  const [hover, setHover] = useState<number | null>(null);
  const total = (d: GroupDatum) => series.reduce((n, sr) => n + (d.values[sr.key] ?? 0), 0);
  const yMax = Math.max(0, ...data.map((d) => (stacked ? total(d) : Math.max(...series.map((sr) => d.values[sr.key] ?? 0)))));
  const h = hover !== null ? data[hover] : null;
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className={s.legend}>
        {series.map((sr) => (
          <span key={sr.key}>
            <span className={s.legendSwatch} style={{ background: sr.color }} />
            {sr.label}
          </span>
        ))}
        {line && (
          <span>
            <span className={s.legendSwatch} style={{ background: 'none', borderTop: '2px dashed var(--text-2)', height: 0, width: 16 }} />
            {line.label}
          </span>
        )}
      </div>
      <Frame
        height={height}
        labels={data.map((d) => d.label)}
        yMax={yMax}
        fixedMax={fixedMax}
        format={axisFormat ?? format}
        ariaLabel={ariaLabel}
        line={line}
        tip={
          h && hover !== null
            ? {
                i: hover,
                v: stacked ? total(h) : Math.max(...series.map((sr) => h.values[sr.key] ?? 0)),
                text: (
                  <>
                    <div>{h.title ?? h.label}</div>
                    {series.map((sr) => (
                      <div key={sr.key}>
                        <span className={s.legendSwatch} style={{ background: sr.color }} />
                        {sr.label} {h.values[sr.key] == null ? '—' : format(h.values[sr.key]!)}
                      </div>
                    ))}
                  </>
                ),
              }
            : null
        }
      >
        {({ slot, x0, y, base }) =>
          data.map((d, i) => {
            const groupW = Math.min(stacked ? 44 : 56, slot * 0.72);
            const left = x0(i) + (slot - groupW) / 2;
            const barW = stacked ? groupW : groupW / Math.max(1, series.length);
            let acc = 0;
            return (
              <g key={d.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                <rect x={x0(i)} y={14} width={slot} height={base - 14} fill="transparent" />
                {series.map((sr, j) => {
                  const v = d.values[sr.key];
                  const x = stacked ? left : left + j * barW + 1;
                  const w = stacked ? barW : barW - 2;
                  if (v == null) return <rect key={sr.key} x={x} y={base - 2} width={w} height={2} fill="none" stroke={sr.color} strokeDasharray="2 2" />;
                  const from = stacked ? acc : 0;
                  acc += v;
                  const yTop = y(from + v);
                  const yBottom = y(from);
                  const hgt = v > 0 ? Math.max(2, yBottom - yTop) : 0;
                  return <rect key={sr.key} className={s.chartBar} x={x} y={yBottom - hgt} width={w} height={hgt} rx={stacked ? 0 : 2} fill={sr.color} />;
                })}
              </g>
            );
          })
        }
      </Frame>
    </div>
  );
}

/** Horizontal funnel: bar length relative to the largest step, with step conversion. */
export function Funnel({ steps, format }: { steps: readonly Datum[]; format: (n: number) => string }) {
  const max = Math.max(1, ...steps.map((d) => d.value));
  return (
    <div className={s.funnel} role="list">
      {steps.map((d, i) => {
        const prev = i > 0 ? steps[i - 1]!.value : null;
        const stepPct = prev ? `上一步的 ${Math.round((d.value / prev) * 100)}%` : prev === 0 ? '—' : null;
        return (
          <div key={d.label} className={s.funnelRow} role="listitem">
            <span className={s.funnelLabel} title={d.title ?? d.label}>
              {d.label}
            </span>
            <div className={s.funnelTrack}>
              <div className={s.funnelFill} style={{ width: `${(d.value / max) * 100}%`, opacity: 1 - i * 0.1 }} />
            </div>
            <span className={s.funnelNums}>
              <strong className="num">{format(d.value)}</strong> <span className="faint small">{i === 0 ? '' : stepPct}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** A one-line stacked meter (budget used, tier mix). */
export function Meter({ parts, ariaLabel }: { parts: ReadonlyArray<{ key: string; value: number; color: string; label?: string }>; ariaLabel: string }) {
  const total = parts.reduce((n, p) => n + p.value, 0);
  return (
    <div className={s.meter} role="img" aria-label={ariaLabel}>
      {total > 0 && parts.map((p) => (p.value > 0 ? <span key={p.key} title={`${p.label ?? p.key}: ${p.value}`} style={{ width: `${(p.value / total) * 100}%`, background: p.color }} /> : null))}
    </div>
  );
}

/** Stable categorical colours for small series (outcomes, tiers). */
export const SERIES = ['var(--chart-1)', 'var(--chart-2)', '#4a463f', '#7c5cd6', '#2f7fb8', '#d97706', '#0f8a7a', '#8a8478'] as const;
export const seriesColor = (i: number) => SERIES[i % SERIES.length]!;
